/**
 * Classifies a FAILED `moolah/chargeMoolahTerminal` dispatch.
 *
 * Both terminal-charge surfaces — PatientProfile's CollectPaymentModal and
 * TxPresenter's PresentDepositPanel — had their own copy of this branch, so a new
 * server response had to be taught to two files or it silently fell into the wrong
 * bucket. That is exactly what happened with the 409 duplicate-charge refusal
 * (payment-service 2026-07-30): a deliberate, correct refusal rendered under
 * "Something went wrong".
 *
 * Three outcomes, because the operator needs three different things:
 *
 *  PENDING — the EXPECTED path. The backend Lambda blocks up to 170s waiting for a
 *    swipe while the axios call gives up at 8s, so an aborted request / 502 / 504
 *    means "still running on the terminal", NOT a failure. Fall back to polling.
 *
 *  BLOCKED — 409. Another charge is already live for this patient or on this
 *    terminal, so we refused to send a second one. Nothing reached the terminal and
 *    NO card was charged by this attempt. Distinct from ERROR because the operator's
 *    next step is "wait for the other one", not "something broke, retry".
 *
 *  ERROR — a real rejection (400/403/404/500…) the operator must see.
 *
 * NOTE on the `!status` branch: a non-HTTP error (say a TypeError thrown in our own
 * code) also has no status and is therefore classified PENDING. That is pre-existing
 * behaviour and is deliberately preserved here — the 8s timeout is the overwhelmingly
 * common case and narrowing it risks breaking the happy path. Flagged, not changed.
 */

import { extractApiError } from './apiError';

export const TERMINAL_CHARGE_OUTCOME = Object.freeze({
  PENDING: 'pending',
  BLOCKED: 'blocked',
  ERROR: 'error',
});

export function classifyTerminalChargeError(err) {
  const status = err && err.response && err.response.status;
  // Checked first so the expected-timeout path keeps its exact prior semantics.
  if (!status || status === 502 || status === 504 || (err && err.code === 'ECONNABORTED')) {
    return TERMINAL_CHARGE_OUTCOME.PENDING;
  }
  if (status === 409) return TERMINAL_CHARGE_OUTCOME.BLOCKED;
  return TERMINAL_CHARGE_OUTCOME.ERROR;
}

/**
 * Pulls the most specific message available.
 *
 * VERIFIED AGAINST THE DEPLOYED LAMBDA (dev, 2026-07-30) — do not "simplify" this
 * to `data.error`. `/opt/common`'s createErrorResponse serialises the message as a
 * BARE JSON STRING, not an object:
 *     { statusCode: 400, body: "\"siteID is required\"" }
 * so axios hands us `response.data === 'siteID is required'` — a string. The
 * payment-service unit-test mock returns `{ error: msg }` instead, so a test can
 * pass against a shape production never sends. The pre-existing frontend code read
 * `err.response?.data?.error` and therefore ALWAYS fell through to axios's generic
 * "Request failed with status code NNN" — no backend error text ever reached the
 * operator on this path.
 *
 * Both shapes are handled: bare string (our Lambdas) and `{message}`/`{error}`
 * objects (API Gateway's own 403/429 bodies, and any handler that returns one).
 */
/**
 * Message extraction is NOT terminal-specific — every Teamio API call has the same
 * problem — so it lives in @/utils/apiError.js and is re-exported here purely so
 * the two terminal surfaces keep a single import. New code should import
 * `extractApiError` directly.
 */
export function terminalChargeErrorMessage(err, fallback = 'Failed to send charge to terminal') {
  return extractApiError(err, fallback);
}
